import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'creativeJunkieLikedItems';
const CHANGE_EVENT = 'creativejunkie:liked-items-change';
const VALID_HANDLE = /^[a-z0-9][a-z0-9-]*$/i;
const productCache = new Map();
let memoryItems = [];
let pageGeneration = 0;
let observer;

function normalizeHandle(value) {
  if (typeof value !== 'string') return '';

  let handle = value.trim();

  if (handle.includes('/products/')) {
    try {
      const url = new URL(handle, window.location.origin);
      handle = url.pathname.split('/products/')[1]?.split('/')[0] || '';
    } catch {
      return '';
    }
  }

  try {
    handle = decodeURIComponent(handle);
  } catch {
    return '';
  }

  return VALID_HANDLE.test(handle) ? handle.toLowerCase() : '';
}

function normalizeItems(value) {
  if (!Array.isArray(value)) return [];

  const unique = new Set();

  for (const item of value) {
    const handle = normalizeHandle(item);
    if (handle) unique.add(handle);
  }

  return Array.from(unique);
}

function readItems() {
  let raw;

  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return [...memoryItems];
  }

  if (raw === null) return [...memoryItems];

  try {
    const parsed = JSON.parse(raw);
    const normalized = normalizeItems(parsed);

    if (!Array.isArray(parsed) || JSON.stringify(normalized) !== JSON.stringify(parsed)) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    }

    memoryItems = normalized;
    return [...normalized];
  } catch {
    try {
      window.localStorage.setItem(STORAGE_KEY, '[]');
    } catch {
      // localStorage can be unavailable in privacy modes; the in-memory fallback remains usable.
    }

    memoryItems = [];
    return [];
  }
}

function writeItems(items, announceChange = true) {
  const normalized = normalizeItems(items);
  memoryItems = normalized;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Keep the current-tab experience functional if localStorage is unavailable.
  }

  if (announceChange) {
    window.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { items: [...normalized] },
      })
    );
  }

  return normalized;
}

function removeItems(handles) {
  const removed = new Set(handles.map(normalizeHandle).filter(Boolean));
  if (!removed.size) return readItems();

  return writeItems(readItems().filter((handle) => !removed.has(handle)));
}

function heartMarkup() {
  return `
    <span class="cj-liked-button__icon" aria-hidden="true">
      <svg class="cj-ui-icon icon-heart" viewBox="0 0 24 24" fill="none" focusable="false">
        <path d="M12 20.25 4.8 13.7A6.7 6.7 0 0 1 2.5 8.75 5.25 5.25 0 0 1 12 5.65a5.25 5.25 0 0 1 9.5 3.1 6.7 6.7 0 0 1-2.3 4.95L12 20.25Z" stroke="currentColor" stroke-width="var(--cj-ui-icon-stroke, 1.75)" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
  `;
}

function createAutomaticButton(card) {
  if (!(card instanceof HTMLElement) || card.querySelector('[data-liked-toggle]')) return;

  const link = card.querySelector('a[href*="/products/"]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const handle = normalizeHandle(link.href);
  if (!handle) return;

  const title =
    card.querySelector('.wear-with__title, .recently-viewed__title')?.textContent?.trim() || 'product';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cj-liked-button cj-liked-button--auto';
  button.dataset.likedToggle = '';
  button.dataset.likedMode = 'auto';
  button.dataset.productHandle = handle;
  button.dataset.productTitle = title;
  button.setAttribute('aria-pressed', 'false');
  button.innerHTML = heartMarkup();
  card.prepend(button);
}

function decorateCustomCards(root = document) {
  if (!(root instanceof Element || root instanceof Document)) return;

  if (root instanceof Element && root.matches('.wear-with__item, .recently-viewed__item')) {
    createAutomaticButton(root);
  }

  root.querySelectorAll('.wear-with__item, .recently-viewed__item').forEach(createAutomaticButton);
}

function syncButton(button, likedItems) {
  if (!(button instanceof HTMLButtonElement)) return;

  const handle = normalizeHandle(button.dataset.productHandle);
  if (!handle) {
    button.hidden = true;
    return;
  }

  const isLiked = likedItems.has(handle);
  const title = button.dataset.productTitle || 'product';
  const label = isLiked ? `Unlike ${title}` : `Like ${title}`;
  button.classList.toggle('is-liked', isLiked);
  button.setAttribute('aria-pressed', String(isLiked));
  button.setAttribute('aria-label', label);
  button.title = label;

  const textLabel = button.querySelector('[data-liked-label]');
  if (textLabel) textLabel.textContent = isLiked ? 'Liked' : 'Like';
}

function syncHeader(count) {
  document.querySelectorAll('[data-liked-count]').forEach((badge) => {
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.toggleAttribute('hidden', count === 0);
  });
}

function syncAll(root = document) {
  decorateCustomCards(root);
  const items = new Set(readItems());

  if (root instanceof Element && root.matches('[data-liked-toggle]')) {
    syncButton(root, items);
  }

  root.querySelectorAll?.('[data-liked-toggle]').forEach((button) => syncButton(button, items));
  syncHeader(items.size);
}

function announce(message) {
  let region = document.querySelector('[data-liked-live]');

  if (!region) {
    region = document.createElement('div');
    region.className = 'cj-liked-items__live';
    region.dataset.likedLive = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
  }

  region.textContent = '';
  window.requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function handleToggle(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const retryButton = target.closest('[data-liked-retry]');
  if (retryButton) {
    event.preventDefault();
    productCache.clear();
    loadLikedPage();
    return;
  }

  const button = target.closest('[data-liked-toggle]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const handle = normalizeHandle(button.dataset.productHandle);
  if (!handle) return;

  const items = readItems();
  const isLiked = items.includes(handle);
  const nextItems = isLiked ? items.filter((item) => item !== handle) : [handle, ...items];

  if (isLiked) {
    button.closest('[data-liked-page-card]')?.remove();
  }

  writeItems(nextItems);

  const title = button.dataset.productTitle || 'Product';
  announce(isLiked ? `${title} removed from liked items.` : `${title} added to liked items.`);
}



function cjNormalizeGalleryValue(value) {
  return (value || '').toString().trim().toLowerCase();
}

function cjGetGalleryImages(card) {
  return Array.from(card.querySelectorAll('[data-cj-card-image]'));
}

function cjGetMatchingGalleryImages(card) {
  const currentColor = cjNormalizeGalleryValue(card.dataset.cjCurrentColor);
  const allImages = cjGetGalleryImages(card);
  if (!allImages.length || !currentColor) return allImages;

  const matching = allImages.filter((image) => {
    const imageColor = cjNormalizeGalleryValue(image.dataset.colorKey);
    return imageColor === currentColor || imageColor === 'all' || imageColor === '';
  });

  return matching.length ? matching : allImages;
}

function cjRenderGalleryDots(card, total, activeIndex) {
  const dots = card.querySelector('[data-cj-gallery-dots]');
  if (!(dots instanceof HTMLElement)) return;

  if (total <= 1) {
    dots.replaceChildren();
    return;
  }

  const fragment = document.createDocumentFragment();
  for (let index = 0; index < total; index += 1) {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = `cj-card-gallery__dot${index === activeIndex ? ' is-active' : ''}`;
    dot.dataset.cjGalleryDot = String(index);
    dot.setAttribute('aria-label', `Show product image ${index + 1}`);
    fragment.append(dot);
  }
  dots.replaceChildren(fragment);
}

function cjShowGalleryImage(card, requestedIndex) {
  if (!(card instanceof HTMLElement)) return;

  const gallery = card.querySelector('.cj-card-gallery');
  const allImages = cjGetGalleryImages(card);
  const matchingImages = cjGetMatchingGalleryImages(card);
  if (!matchingImages.length) return;

  let activeIndex = Number.parseInt(String(requestedIndex), 10);
  if (!Number.isFinite(activeIndex)) activeIndex = 0;
  if (activeIndex < 0) activeIndex = matchingImages.length - 1;
  if (activeIndex >= matchingImages.length) activeIndex = 0;

  card.dataset.cjActiveIndex = String(activeIndex);
  allImages.forEach((image) => {
    image.classList.remove('is-visible');
    image.hidden = true;
  });

  const activeImage = matchingImages[activeIndex];
  activeImage.hidden = false;
  window.requestAnimationFrame(() => activeImage.classList.add('is-visible'));

  gallery?.classList.toggle('has-multiple-images', matchingImages.length > 1);
  cjRenderGalleryDots(card, matchingImages.length, activeIndex);
}

function initializeLikedGalleries(root = document) {
  root.querySelectorAll?.('[data-cj-card-gallery]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    cjShowGalleryImage(card, Number.parseInt(card.dataset.cjActiveIndex || '0', 10));
    card.dataset.cjInitialized = 'true';
  });
}

function installLikedGalleryRuntime() {
  if (window.creativeJunkieCardGalleryReady || window.creativeJunkieLikedGalleryReady) return;
  window.creativeJunkieLikedGalleryReady = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('[data-cj-gallery-arrow]');
    const dot = event.target.closest('[data-cj-gallery-dot]');
    if (!arrow && !dot) return;

    const card = event.target.closest('[data-cj-card-gallery]');
    if (!(card instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (dot instanceof HTMLElement) {
      cjShowGalleryImage(card, Number.parseInt(dot.dataset.cjGalleryDot || '0', 10));
      dot.blur();
      return;
    }

    if (arrow instanceof HTMLElement) {
      const currentIndex = Number.parseInt(card.dataset.cjActiveIndex || '0', 10);
      const direction = arrow.dataset.cjGalleryArrow === 'prev' ? -1 : 1;
      cjShowGalleryImage(card, currentIndex + direction);
      arrow.blur();
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('[data-cj-card-swatch]');
    if (!(swatch instanceof HTMLElement)) return;

    const productCard = swatch.closest('product-card');
    const gallery = productCard?.querySelector('[data-cj-card-gallery]');
    if (!(gallery instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    gallery.dataset.cjCurrentColor = cjNormalizeGalleryValue(
      swatch.dataset.colorKey || swatch.getAttribute('title') || ''
    );
    cjShowGalleryImage(gallery, 0);

    productCard.querySelectorAll('[data-cj-card-swatch]').forEach((item) => {
      item.classList.toggle('is-active', item === swatch);
      if (item === swatch) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });

    const variantUrl = swatch.dataset.variantUrl || swatch.getAttribute('href');
    if (variantUrl) {
      productCard
        .querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"], .cj-card-view-details')
        .forEach((link) => link.setAttribute('href', variantUrl));
    }
  });

  let touchStartX = 0;
  document.addEventListener(
    'touchstart',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('[data-cj-card-gallery]')) return;
      touchStartX = event.touches[0]?.clientX || 0;
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const card = event.target.closest('[data-cj-card-gallery]');
      if (!(card instanceof HTMLElement)) return;
      const difference = (event.changedTouches[0]?.clientX || 0) - touchStartX;
      if (Math.abs(difference) < 40) return;
      const currentIndex = Number.parseInt(card.dataset.cjActiveIndex || '0', 10);
      cjShowGalleryImage(card, currentIndex + (difference > 0 ? -1 : 1));
    },
    { passive: true }
  );
}

function getProductPageUrl(handle) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}products/${encodeURIComponent(handle)}`;
}

function getRenderedCardUrl(handle) {
  const url = new URL(getProductPageUrl(handle), window.location.origin);
  url.searchParams.set('section_id', 'liked-item-product-card');

  const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
  if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

  return url.toString();
}

function createRenderedGridItem(html, handle) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();

  const rendered = template.content.querySelector('[data-liked-rendered-card]');
  const productCard = rendered?.querySelector('product-card');
  if (!(productCard instanceof HTMLElement)) return null;

  const item = document.createElement('li');
  item.className = 'product-grid__item';
  item.dataset.likedPageCard = '';
  item.dataset.productHandle = handle;
  item.append(productCard);
  return item;
}

async function fetchLikedProductCard(handle) {
  if (productCache.has(handle)) return productCache.get(handle);

  const request = (async () => {
    try {
      const response = await fetch(getRenderedCardUrl(handle), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (response.status === 404) return { handle, invalid: true };
      if (!response.ok) return { handle, error: true };

      const html = await response.text();
      const item = createRenderedGridItem(html, handle);
      if (!item) return { handle, error: true };

      return { handle, item };
    } catch {
      return { handle, error: true };
    }
  })();

  productCache.set(handle, request);
  const result = await request;
  if (!result.item) productCache.delete(handle);
  return result;
}

function setLikedPageState(page, { loading, rendered, saved, failed }) {
  const grid = page.querySelector('[data-liked-grid]');
  const loadingState = page.querySelector('[data-liked-loading]');
  const emptyState = page.querySelector('[data-liked-empty]');
  const errorState = page.querySelector('[data-liked-error]');
  const warningState = page.querySelector('[data-liked-warning]');

  loadingState?.toggleAttribute('hidden', !loading);
  grid?.toggleAttribute('hidden', loading || rendered === 0);
  emptyState?.toggleAttribute('hidden', loading || saved > 0);
  warningState?.toggleAttribute('hidden', loading || rendered === 0 || failed === 0);
  errorState?.toggleAttribute('hidden', loading || rendered > 0 || saved === 0 || failed === 0);
}

async function loadLikedPage() {
  const page = document.querySelector('[data-liked-items-page]');
  if (!(page instanceof HTMLElement)) return;

  const grid = page.querySelector('[data-liked-grid]');
  if (!(grid instanceof HTMLElement)) return;

  const generation = ++pageGeneration;
  const handles = readItems();

  setLikedPageState(page, {
    loading: handles.length > 0,
    rendered: 0,
    saved: handles.length,
    failed: 0,
  });

  if (!handles.length) {
    grid.replaceChildren();
    return;
  }

  const results = [];
  for (let index = 0; index < handles.length; index += 4) {
    const batch = handles.slice(index, index + 4);
    results.push(...(await Promise.all(batch.map(fetchLikedProductCard))));
  }

  if (generation !== pageGeneration) return;

  const invalid = results.filter((result) => result.invalid).map((result) => result.handle);
  const failed = results.filter((result) => result.error).length;
  const items = results.filter((result) => result.item).map((result) => result.item);

  grid.replaceChildren(...items);
  installLikedGalleryRuntime();
  initializeLikedGalleries(grid);
  syncAll(grid);

  if (invalid.length) {
    removeItems(invalid);
    return;
  }

  setLikedPageState(page, {
    loading: false,
    rendered: items.length,
    saved: readItems().length,
    failed,
  });
}

function observeDynamicContent() {
  observer?.disconnect();
  observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) syncAll(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function handleItemsChanged() {
  syncAll();
  loadLikedPage();
}

function init() {
  readItems();
  installLikedGalleryRuntime();
  syncAll();
  observeDynamicContent();
  loadLikedPage();
}

document.addEventListener('click', handleToggle, true);
document.addEventListener('shopify:section:load', (event) => {
  syncAll(event.target);
  loadLikedPage();
});
window.addEventListener(CHANGE_EVENT, handleItemsChanged);
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) handleItemsChanged();
});
window.addEventListener('pageshow', handleItemsChanged);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
